/**
 * The HTTP client every PostgREST request goes through.
 *
 * Its whole job is two headers:
 *
 *   Authorization    the minted token, whose `role` claim PostgREST turns
 *                    into SET LOCAL ROLE
 *   Accept-Profile   which schema to read (Content-Profile to write)
 *
 * BOTH COME FROM THE SAME MINTED TOKEN. The schema is never taken from a URL,
 * a route param or anything else the user can influence, because then the
 * client would be choosing its own tenant. It cannot be trusted to do that
 * even if we wrote it correctly today: the guarantee has to hold when this
 * file has a bug.
 *
 * It does not need to be trusted. A token for crm_org_9 paired with
 * `Accept-Profile: crm_org_7` gets:
 *
 *   403 {"code":"42501","message":"permission denied for schema crm_org_7"}
 *
 * from Postgres, because the role holds USAGE on exactly one schema. Setting
 * the header from the token is what makes the app WORK; the grant is what
 * makes it SAFE. Those are separate, and only the second one is load-bearing.
 */

import { fetchUtils } from "ra-core";

import { getCrmConfig } from "@/lib/crmConfig";
import { clearToken, getToken } from "./token";

/** PostgREST wants Content-Profile on writes, Accept-Profile on reads. */
const isWrite = (method?: string) =>
  !!method && !["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase());

export const createHttpClient = (keycloakToken: () => Promise<string>) => {
  return async (url: string, options: fetchUtils.Options = {}) => {
    const [cfg, minted] = await Promise.all([
      getCrmConfig(),
      getToken(keycloakToken),
    ]);
    void cfg;

    const headers = new Headers(
      (options.headers as HeadersInit) ?? { Accept: "application/json" },
    );
    headers.set("Authorization", `Bearer ${minted.token}`);
    headers.set(
      isWrite(options.method) ? "Content-Profile" : "Accept-Profile",
      minted.role,
    );

    try {
      return await fetchUtils.fetchJson(url, { ...options, headers });
    } catch (e: any) {
      // A 401 here means the minted token expired mid-session. Drop it so the
      // next call re-mints rather than retrying the same dead token forever —
      // otherwise the UI shows a permanent error that a reload would fix,
      // which is the worst kind because users learn to reload reflexively.
      if (e?.status === 401) clearToken();
      throw e;
    }
  };
};
